// ─── src/components/dashboard/EditApplicantModal.tsx ──────────────────────────
import { useEffect, useState, type FC, type ChangeEvent } from "react";
import { X, Save, Loader2 } from "lucide-react";
import type { Registration } from "../../types/registration";
import { avatarColor, initials } from "../../utils/formatter";
import { useUpdateRegistration } from "../../hooks/useRegistrations";

interface Props {
  reg: Registration;
  onClose: () => void;
}

const CITIES = [
  "Damascus", "Aleppo", "Lattakia", "Homs",
  "Hama", "Daraa", "Idlib", "Deir ez-Zur",
];

const Field: FC<{
  label: string;
  children: React.ReactNode;
}> = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <label className="text-xs text-brand-muted font-medium">{label}</label>
    {children}
  </div>
);

const inputCls =
  "bg-brand-navy border border-brand-border text-white text-sm rounded-lg " +
  "px-3 py-2 focus:outline-none focus:border-brand-orange transition-colors w-full";

const selectCls =
  "bg-brand-navy border border-brand-border text-white text-sm rounded-lg " +
  "px-3 py-2 focus:outline-none focus:border-brand-orange transition-colors w-full cursor-pointer";

export const EditApplicantModal: FC<Props> = ({ reg, onClose }) => {
  const { mutate, isPending, error } = useUpdateRegistration();

  const [form, setForm] = useState({
    fullName:      reg.fullName,
    email:         reg.email,
    phone:         reg.phone,
    city:          reg.city ?? "",
    confirmedCity: reg.confirmedCity ?? "",
    status:        reg.status ?? "pending",
  });

  // Close on Esc
  useEffect(() => {
    const handler = (e: KeyboardEvent) => e.key === "Escape" && !isPending && onClose();
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, isPending]);

  const set = (key: keyof typeof form) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = () => {
    mutate(
      { id: reg.id, dto: form },
      { onSuccess: onClose },
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4
                 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && !isPending && onClose()}
    >
      <div className="card w-full max-w-lg shadow-2xl shadow-black/40
                      animate-in fade-in-0 slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-brand-border">
          <div className="flex items-center gap-3">
            <div
              className={`${avatarColor(reg.fullName)} w-10 h-10 rounded-xl
                          flex items-center justify-center text-white text-sm font-bold shrink-0`}
            >
              {initials(reg.fullName)}
            </div>
            <div>
              <h2 className="text-base font-bold text-white">Edit Applicant</h2>
              <p className="text-brand-muted text-xs">{reg.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isPending}
            className="p-2 rounded-xl text-brand-muted hover:text-white
                       hover:bg-brand-border transition-colors disabled:opacity-40"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name">
              <input
                type="text"
                value={form.fullName}
                onChange={set("fullName")}
                className={inputCls}
              />
            </Field>

            <Field label="Email">
              <input
                type="email"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
              />
            </Field>

            <Field label="Phone">
              <input
                type="text"
                value={form.phone}
                onChange={set("phone")}
                className={inputCls}
              />
            </Field>

            <Field label="App. Status">
              <select value={form.status} onChange={set("status")} className={selectCls}>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </Field>

            <Field label="City">
              <select value={form.city} onChange={set("city")} className={selectCls}>
                <option value="">— Select city —</option>
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Confirmed City">
              <select value={form.confirmedCity} onChange={set("confirmedCity")} className={selectCls}>
                <option value="">— None —</option>
                {CITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20
                          rounded-lg px-3 py-2">
              {(error as Error).message}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button
            onClick={onClose}
            disabled={isPending}
            className="px-4 py-2 rounded-xl text-sm text-brand-muted
                       hover:text-white hover:bg-brand-border transition-colors
                       disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isPending}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm
                       font-medium bg-brand-green text-gray-900
                       hover:bg-brand-greenHover transition-colors
                       disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Save size={14} />
            )}
            {isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};
